import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { formatNumberPrecise } from '../Utils';

const ITEMS_PER_PAGE = 100;

const GiftHistory = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchPage = useCallback(async (pageNum, term) => {
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({ page: String(pageNum), limit: String(ITEMS_PER_PAGE) });
            if (term) params.set('q', term.trim());
            const data = await apiGet(`/api/admin/gift-history?${params.toString()}`);
            setLogs(data.logs || []);
            setHasNextPage(!!data.hasNext);
            setTotal(data.total || 0);
        } catch (err) {
            setError('Failed to load gift history.');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { setCurrentPage(1); fetchPage(1, searchTerm); }, [searchTerm, fetchPage]);

    const handleNextPage = () => { const n = currentPage + 1; setCurrentPage(n); fetchPage(n, searchTerm); };
    const handlePrevPage = () => { const p = currentPage - 1; setCurrentPage(p); fetchPage(p, searchTerm); };
    const handleSearch = (e) => { e.preventDefault(); setSearchTerm(searchInput); };
    const copyToClipboard = (text) => { if (text) navigator.clipboard.writeText(text); };

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Gift History...</p></div>;
    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <div>
                    <h2 style={{ margin: 0 }}>Gift Transactions</h2>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>Live streaming gift history</p>
                </div>
                <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
                    <input
                        type="text"
                        className="dark-input"
                        style={{ width: 260 }}
                        placeholder="Search by ID (Sent & Received)..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    <button type="submit" style={{
                        background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', color: '#fff',
                        border: 'none', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontSize: 13
                    }}>
                        <i className="fas fa-search" />
                    </button>
                </form>
            </div>

            {searchTerm && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                    <span>Results for <strong style={{ color: '#fff' }}>{searchTerm}</strong> — {total} total</span>
                    <span className="badge badge-yellow"><i className="fas fa-arrow-up" style={{ marginRight: 4 }} />Sent</span>
                    <span className="badge badge-blue"><i className="fas fa-arrow-down" style={{ marginRight: 4 }} />Received</span>
                </div>
            )}

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            {searchTerm && <th className="text-center">Type</th>}
                            <th>Time</th>
                            <th>Sender (ID)</th>
                            <th>Receiver (ID)</th>
                            <th>Gift Name</th>
                            <th className="text-center">Qty</th>
                            <th>Total Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                {searchTerm && (
                                    <td className="text-center">
                                        {log._direction === 'sent'
                                            ? <span className="badge badge-yellow"><i className="fas fa-arrow-up" style={{ marginRight: 4 }} />Sent</span>
                                            : <span className="badge badge-blue"><i className="fas fa-arrow-down" style={{ marginRight: 4 }} />Rcvd</span>
                                        }
                                    </td>
                                )}
                                <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{log.formattedTime}</td>
                                <td>
                                    <span
                                        onClick={() => copyToClipboard(log.senderCustomId || log.senderId)}
                                        style={{
                                            background: 'rgba(255,77,109,0.15)', color: '#fca5a5',
                                            border: '1px solid rgba(255,77,109,0.25)',
                                            borderRadius: 5, padding: '2px 8px', fontSize: 12,
                                            fontFamily: 'monospace', cursor: 'pointer', fontWeight: 700
                                        }}
                                        title="Click to copy"
                                    >
                                        {log.senderCustomId || log.senderId}
                                    </span>
                                </td>
                                <td>
                                    <span
                                        onClick={() => copyToClipboard(log.recipientCustomId || log.recipientId)}
                                        style={{
                                            background: 'rgba(34,197,94,0.12)', color: '#4ade80',
                                            border: '1px solid rgba(34,197,94,0.25)',
                                            borderRadius: 5, padding: '2px 8px', fontSize: 12,
                                            fontFamily: 'monospace', cursor: 'pointer', fontWeight: 700
                                        }}
                                        title="Click to copy"
                                    >
                                        {log.recipientCustomId || log.recipientId}
                                    </span>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {log.giftImage
                                            ? <img src={log.giftImage} alt={log.giftName} style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'contain', background: 'rgba(255,255,255,0.05)' }} />
                                            : <div style={{ width: 30, height: 30, borderRadius: 6, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)' }}><i className="fas fa-gift" /></div>
                                        }
                                        <span style={{ fontSize: 13 }}>{log.giftName}</span>
                                    </div>
                                </td>
                                <td className="text-center">
                                    <span className="badge badge-blue">{log.quantity}</span>
                                </td>
                                <td style={{ fontWeight: 700, color: '#c4b5fd' }}>
                                    💎 {formatNumberPrecise(log.totalPrice)}
                                </td>
                            </tr>
                        ))}
                        {logs.length === 0 && <tr className="empty-row"><td colSpan={searchTerm ? 7 : 6}>No gift transactions found.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="dark-pagination">
                <button className="btn-page" onClick={handlePrevPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left" style={{ marginRight: 6 }} />Previous
                </button>
                <span>Page {currentPage}</span>
                <button className="btn-page" onClick={handleNextPage} disabled={!hasNextPage}>
                    Next<i className="fas fa-chevron-right" style={{ marginLeft: 6 }} />
                </button>
            </div>
        </div>
    );
};

export default GiftHistory;
